import React from 'react';
import { X, Building2, Calculator, BarChart3, Users, Mail, FileText } from 'lucide-react';
import { Button } from '../ui/button';

const formatCHF = (val) => {
  if (val == null || val === '') return '-';
  return `CHF ${Math.round(Number(val)).toLocaleString('de-CH')}`;
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-[#1a1c1b] uppercase tracking-wider mb-3 flex items-center gap-2 border-b border-gray-200 pb-2">
      <Icon size={16} className="text-[#FF0000]" />
      {title}
    </h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
      {children}
    </div>
  </div>
);

const Field = ({ label, value }) => (
  <div className="py-1">
    <dt className="text-xs text-[#888b89]">{label}</dt>
    <dd className="text-sm font-medium text-[#1a1c1b]" data-testid={`detail-${label.toLowerCase().replace(/[\s()\/]+/g, '-')}`}>
      {value || '-'}
    </dd>
  </div>
);

const QualBar = ({ label, value }) => (
  <div className="py-1 col-span-1">
    <dt className="text-xs text-[#888b89]">{label}</dt>
    <dd className="flex items-center gap-2 mt-0.5">
      <div className="flex gap-0.5">
        {[1,2,3,4,5].map(i => (
          <div key={i} className={`w-4 h-2 rounded-sm ${i <= value ? 'bg-[#FF0000]' : 'bg-gray-200'}`} />
        ))}
      </div>
      <span className="text-xs font-medium text-[#5a5d5b]">{value}/5</span>
    </dd>
  </div>
);

const ValuationDetailPanel = ({ valuation, onClose }) => {
  if (!valuation) return null;

  const rd = valuation.request_data || {};
  const basic = rd.basic_info || {};
  const fin = rd.financial_data || {};
  const qual = rd.qualitative_factors || {};
  const ctx = rd.context || {};
  const contact = rd.contact || {};
  const res = valuation.result_data || {};
  const methods = res.methods || {};

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="valuation-detail-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-[#1a1c1b]" data-testid="detail-company-name">
              {basic.company_name || valuation.company_name || 'Bewertung'}
            </h2>
            <p className="text-sm text-[#888b89]">
              {valuation.created_at ? new Date(valuation.created_at).toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="detail-close-btn">
            <X size={20} />
          </Button>
        </div>

        <div className="px-6 py-4">
          {/* Bewertungsergebnis (prominent at top) */}
          <div className="mb-6 p-4 bg-gradient-to-r from-[#FFF5F5] to-white rounded-xl border border-[#FFE5E5]">
            <h3 className="text-sm font-semibold text-[#CC0000] uppercase tracking-wider mb-3 flex items-center gap-2">
              <Calculator size={16} />
              Bewertungsergebnis
            </h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <div className="text-xs text-[#888b89] mb-1">Unterer Wert</div>
                <div className="text-sm font-bold text-[#1a1c1b]" data-testid="detail-value-low">{formatCHF(res.value_low || valuation.value_low)}</div>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm border-2 border-[#FF0000]">
                <div className="text-xs text-[#FF0000] mb-1 font-semibold">Mittlerer Wert</div>
                <div className="text-base font-bold text-[#FF0000]" data-testid="detail-value-mid">{formatCHF(res.value_mid || valuation.value_mid)}</div>
              </div>
              <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                <div className="text-xs text-[#888b89] mb-1">Oberer Wert</div>
                <div className="text-sm font-bold text-[#1a1c1b]" data-testid="detail-value-high">{formatCHF(res.value_high || valuation.value_high)}</div>
              </div>
            </div>

            {/* Method breakdown */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'earnings_value', label: 'Ertragswert' },
                { key: 'ebit_multiple', label: 'EBIT-Multiple' },
                { key: 'asset_value', label: 'Substanzwert' },
                { key: 'practitioner_value', label: 'Praktikermethode' }
              ].map(m => {
                const method = methods[m.key] || {};
                return (
                  <div key={m.key} className="flex justify-between items-center px-3 py-2 bg-white rounded-lg text-sm">
                    <span className="text-[#5a5d5b]">{m.label}</span>
                    <span className="font-semibold text-[#1a1c1b]" data-testid={`detail-method-${m.key}`}>{formatCHF(method.value)}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex gap-4 text-xs text-[#888b89]">
              <span>Risikokategorie: <strong className="text-[#1a1c1b]">{res.risk_category || valuation.risk_category || '-'}/5</strong></span>
              <span>Risikoanpassung: <strong className="text-[#1a1c1b]">{res.risk_adjustment ? `${(res.risk_adjustment * 100).toFixed(0)}%` : '-'}</strong></span>
            </div>
          </div>

          {/* Unternehmensangaben */}
          <Section icon={Building2} title="Unternehmensangaben">
            <Field label="Firma" value={basic.company_name || valuation.company_name} />
            <Field label="Branche" value={basic.industry || valuation.industry} />
            <Field label="Standort" value={basic.location} />
            <Field label="Gründungsjahr" value={basic.founding_year} />
            <Field label="Mitarbeiter" value={basic.employees} />
            <Field label="Geschäftsjahresende" value={basic.fiscal_year_end} />
          </Section>

          {/* Finanzkennzahlen */}
          <Section icon={BarChart3} title="Finanzkennzahlen">
            <Field label="Umsatz" value={formatCHF(fin.revenue)} />
            <Field label="Reingewinn" value={formatCHF(fin.net_profit)} />
            <Field label="Abschreibungen" value={formatCHF(fin.depreciation)} />
            <Field label="Liquide Mittel" value={formatCHF(fin.liquid_assets)} />
            <Field label="Umlaufvermögen" value={formatCHF(fin.current_assets)} />
            <Field label="Anlagevermögen" value={formatCHF(fin.fixed_assets)} />
            <Field label="Kurzfr. FK" value={formatCHF(fin.short_term_debt)} />
            <Field label="Langfr. FK" value={formatCHF(fin.long_term_debt)} />
            <Field label="Eigenkapital" value={formatCHF(fin.equity)} />
            <Field label="Stille Reserven" value={formatCHF(fin.silent_reserves)} />
          </Section>

          {/* Qualitative Faktoren */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-[#1a1c1b] uppercase tracking-wider mb-3 flex items-center gap-2 border-b border-gray-200 pb-2">
              <FileText size={16} className="text-[#FF0000]" />
              Qualitative Faktoren
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
              <QualBar label="Management" value={qual.management_quality} />
              <QualBar label="Marktposition" value={qual.market_position} />
              <QualBar label="Technologie" value={qual.technology} />
              <QualBar label="Innovation" value={qual.innovation} />
              <QualBar label="Nachhaltigkeit" value={qual.sustainability} />
              <QualBar label="Preissetzung" value={qual.pricing_power} />
              <QualBar label="Qualität" value={qual.quality} />
              <QualBar label="Marke" value={qual.brand} />
              <QualBar label="Prozesse" value={qual.processes} />
            </div>
          </div>

          {/* Bewertungskontext */}
          <Section icon={FileText} title="Bewertungskontext">
            <Field label="Zweck" value={ctx.purpose || valuation.purpose} />
            <Field label="Zeitrahmen" value={ctx.timeline} />
            <Field label="Anmerkungen" value={ctx.additional_notes} />
          </Section>

          {/* Kontaktdaten */}
          <Section icon={Users} title="Kontaktdaten">
            <Field label="Vorname" value={contact.first_name} />
            <Field label="Nachname" value={contact.last_name} />
            <Field label="E-Mail" value={contact.email || valuation.contact_email} />
            <Field label="Telefon" value={contact.phone} />
            <Field label="Position" value={contact.company_position} />
          </Section>

          {/* Status */}
          <Section icon={Mail} title="Status">
            <Field label="E-Mail versendet" value={valuation.email_sent ? 'Ja' : 'Nein'} />
            <Field label="KI-Analyse" value={valuation.ai_commentary ? 'Ja' : 'Nein'} />
          </Section>
        </div>
      </div>
    </div>
  );
};

export default ValuationDetailPanel;
